import { useRestaurantOpenStatus } from '@/features/opening-hours/hooks/useRestaurantOpenStatus';
import {
  useReservationConfig,
  useUpdateReservationConfig,
} from '@/features/reservation-setup/hooks/useReservationConfig';
import { useT } from '@/i18n';
import { endOfTodayIso, formatTime, isPauseActive } from '@/utils/datetime';
import { f7 } from 'framework7-react';

/**
 * Compact reservation-availability strip, pinned above the reservations
 * workspace. A single tappable cell showing the *effective* booking state right
 * now - Open / Closed (by RESERVATION hours) / Paused - plus when it next
 * changes, so the operator can read whether new bookings are being taken at a
 * glance and tap to pause or resume. Mirrors the online-order OrderPauseCard,
 * but reservations have a single channel and only a `pausedUntil` instant (no
 * stored TIMED / UNTIL_CLOSE mode) - the label derives purely from that instant.
 * Fine-grained config lives in Settings → Reservation Setup.
 */
export default function ReservationPauseCard() {
  const t = useT();
  const rp = t.reservations.pause;
  const { data: config } = useReservationConfig();
  const openStatus = useRestaurantOpenStatus('RESERVATION');
  const update = useUpdateReservationConfig();
  const busy = update.isPending;

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  const pausedUntil = config?.pausedUntil ?? null;
  const paused = isPauseActive(pausedUntil);
  // A pause set via "until close" lands on the end of the local day (23:59), so
  // we can label it "close" without storing a separate mode. Comparing the local
  // HH:mm (not the raw ISO) stays robust if the server drops the milliseconds.
  const untilClose = !!pausedUntil && formatTime(pausedUntil) === '23:59';
  const isOpen = openStatus?.isOpen ?? true;
  // Master switch (Settings → Reservation Setup): when off, the restaurant takes
  // no reservations at all, so this outranks hours and any temporary pause - a
  // "paused" or green "open" label here would be a lie. Undefined while loading ⇒
  // don't flash "Off"; treat as on until the config arrives.
  const disabled = config ? !config.enabled : false;

  const cssState = disabled ? 'is-closed' : paused ? 'is-paused' : isOpen ? 'is-live' : 'is-closed';

  // Off wins, then paused; otherwise reuse the open-status label, which already
  // reads "Open · until 22:00" / "Closed · opens 11:00" (falls back to a bare
  // word when no hours are configured at all).
  const statusText = disabled
    ? rp.off
    : paused
      ? untilClose
        ? rp.pausedUntilClose
        : rp.pausedUntilTime(formatTime(pausedUntil!))
      : (openStatus?.label ?? rp.open);

  const pauseFor = (minutes: number) =>
    update.mutate(
      { pausedUntil: new Date(Date.now() + minutes * 60_000).toISOString() },
      { onSuccess: () => toast(rp.toast.pausedFor(minutes)) },
    );
  const pauseUntilClose = () =>
    update.mutate(
      { pausedUntil: endOfTodayIso() },
      { onSuccess: () => toast(rp.toast.pausedUntilClose) },
    );
  const resume = () =>
    update.mutate({ pausedUntil: null }, { onSuccess: () => toast(rp.toast.resumed) });

  const openActions = (targetEl: HTMLElement) => {
    type ActionButton = { text: string; label?: boolean; color?: string; onClick?: () => void };
    const group: ActionButton[] = [
      { label: true, text: rp.action.heading },
      { text: rp.action.pause30, onClick: () => pauseFor(30) },
      { text: rp.action.pause1hour, onClick: () => pauseFor(60) },
      { text: rp.action.pauseHours(2), onClick: () => pauseFor(120) },
      { text: rp.action.pauseHours(3), onClick: () => pauseFor(180) },
      { text: rp.action.pauseHours(4), onClick: () => pauseFor(240) },
      { text: rp.action.pauseUntilClose, onClick: pauseUntilClose },
    ];
    if (paused) {
      group.push({ text: rp.action.resumeNow, color: 'green', onClick: resume });
    }
    // Anchor to the tapped cell: on regular-width (iPad/desktop) F7 converts the
    // sheet to a popover that closes on outside click; without a target it stays a
    // bottom sheet even on desktop and the backdrop dismiss is unreliable (same
    // pattern as the view/status pickers on the reservations page).
    f7.actions
      .create({ targetEl, buttons: [[...group, { text: rp.action.cancel, color: 'red' }]] })
      .open();
  };

  return (
    // Single channel → one full-width cell (the order strip is a 2-col grid).
    // Extra top margin so the cell clears the date-strip subnavbar above it
    // (the shared `.ord-health` 4px top is enough on Orders, which has no strip).
    <div className="ord-health" style={{ gridTemplateColumns: '1fr', marginTop: 14 }}>
      <button
        type="button"
        className={`ord-health-cell ${cssState}`}
        disabled={busy}
        onClick={(e) => openActions(e.currentTarget)}
      >
        <span className="ord-health-cell__top">
          <span className="ord-health-cell__label">{rp.cellLabel}</span>
          <i className="f7-icons ord-health-cell__chevron">chevron_down</i>
        </span>
        <span className="ord-health-cell__status">
          <span className="ord-health-dot" />
          <span className="ord-health-cell__status-text">{statusText}</span>
        </span>
      </button>
    </div>
  );
}
